import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaFilter,
  FaInfoCircle,
  FaPlus,
  FaRedoAlt,
  FaSave,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUserShield,
  FaUsers,
} from "react-icons/fa";
import api from "../lib/api";
import "./CSS/UsersPremium.css";

const roleOptions = [
  { value: "operator", label: "Operator" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
  { value: "customer", label: "Customer" },
];

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "error" || notice.type === "warning"
      ? FaExclamationCircle
      : FaInfoCircle;

  return (
    <div
      className={`usr-toast ${notice.type}`}
      role={notice.type === "error" || notice.type === "warning" ? "alert" : "status"}
      aria-live={notice.type === "error" || notice.type === "warning" ? "assertive" : "polite"}
    >
      <div className="usr-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="usr-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="usr-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function DeleteConfirm({ open, name, onClose, onConfirm, saving }) {
  if (!open) return null;

  return (
    <div className="usr-overlay">
      <div className="usr-modal usr-confirm-modal">
        <div className="usr-modal-head">
          <div>
            <span>Confirm</span>
            <h3>Delete user?</h3>
          </div>
          <button type="button" className="usr-modal-close" onClick={onClose}>
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        <div className="usr-modal-body">
          <p className="usr-confirm-text">
            This will remove <strong>{name || "this user"}</strong> from the system.
          </p>
        </div>

        <div className="usr-modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={saving}>
            <FaTrash aria-hidden="true" />
            {saving ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Users() {
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    role: "operator",
    password: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const usersPerPage = 5;

  const showNotice = (type, title, message) => {
    setNotice({
      id: Date.now(),
      message,
      title,
      type,
    });
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(
      () => setNotice(null),
      notice.type === "warning" || notice.type === "error" ? 5200 : 3600
    );
    return () => clearTimeout(timer);
  }, [notice]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/users?scope=tenant", {
        headers,
      });
      setUsers(res.data || []);
    } catch (err) {
      console.error("Users load error:", err);
      showNotice(
        "error",
        "Load failed",
        err?.response?.data?.error || "Unable to load users right now."
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: "", phone: "", role: "operator", password: "" });
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      showNotice("warning", "Name required", "Please enter a user name.");
      return false;
    }
    if (!form.phone.trim()) {
      showNotice("warning", "Phone required", "Please enter a mobile number.");
      return false;
    }
    if (!form.password.trim()) {
      showNotice("warning", "Password required", "Please enter a password.");
      return false;
    }
    return true;
  };

  const saveUser = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      await api.post("/api/users", form, {
        headers,
      });
      showNotice("success", "Saved", "User created successfully.");
      resetForm();
      await fetchUsers();
    } catch (err) {
      console.error("Users save error:", err);
      showNotice(
        "error",
        "Save failed",
        err?.response?.data?.error || "Unable to save this user right now."
      );
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (user) => setDeleteTarget(user);

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;

    try {
      setSaving(true);
      await api.delete(`/api/users/${deleteTarget.id}?scope=tenant`, {
        headers,
      });
      showNotice("success", "Deleted", "User removed successfully.");
      setDeleteTarget(null);
      await fetchUsers();
    } catch (err) {
      console.error("Users delete error:", err);
      showNotice(
        "error",
        "Delete failed",
        err?.response?.data?.error || "Unable to delete this user right now."
      );
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((user) => {
      const matchesRole = !roleFilter || user.role === roleFilter;
      const matchesQuery =
        !q ||
        String(user.name || "").toLowerCase().includes(q) ||
        String(user.phone || "").toLowerCase().includes(q) ||
        String(user.role || "").toLowerCase().includes(q);
      return matchesRole && matchesQuery;
    });
  }, [roleFilter, search, users]);

  const totalPages = Math.max(Math.ceil(filteredUsers.length / usersPerPage), 1);
  const currentUsers = useMemo(() => {
    const start = (currentPage - 1) * usersPerPage;
    return filteredUsers.slice(start, start + usersPerPage);
  }, [currentPage, filteredUsers]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, roleFilter]);

  const roleCounts = useMemo(
    () => ({
      operator: users.filter((u) => u.role === "operator").length,
      manager: users.filter((u) => u.role === "manager").length,
      admin: users.filter((u) => u.role === "admin").length,
      customer: users.filter((u) => u.role === "customer").length,
    }),
    [users]
  );

  const summaryCards = [
    { label: "Users", value: users.length, helper: "All accounts in system", tone: "blue", icon: FaUsers },
    {
      label: "Operators",
      value: roleCounts.operator,
      helper: "Accounts used for station work",
      tone: "green",
      icon: FaUserShield,
    },
    {
      label: "Managers",
      value: roleCounts.manager,
      helper: "Accounts with broader access",
      tone: "amber",
      icon: FaFilter,
    },
    {
      label: "Visible Rows",
      value: filteredUsers.length,
      helper: "Matches search and filter",
      tone: "slate",
      icon: FaSearch,
    },
  ];

  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages));
  };

  const getRoleTone = (role) => {
    const value = String(role || "").toLowerCase();
    if (value === "admin") return "admin";
    if (value === "manager") return "manager";
    if (value === "customer") return "customer";
    return "operator";
  };

  const handleRoleChange = (value) => {
    setForm((prev) => ({
      ...prev,
      role: value,
    }));
  };

  return (
    <div className="usr-page text-light">
      <NotificationToast notice={notice} onClose={() => setNotice(null)} />

      <section className="usr-hero">
        <div>
          <div className="usr-eyebrow">User Control</div>
          <h2>Create user accounts first</h2>
          <p>
            This is the first stop before adding employee details. Create the login here, then link
            it from the employee screen.
          </p>
          <div className="usr-hero-meta">
            <span>{users.length} users</span>
            <span>{roleCounts.operator} operators</span>
            <span>{roleCounts.manager} managers</span>
          </div>
        </div>

        <div className="usr-hero-actions">
          <button className="btn usr-secondary-action" type="button" onClick={fetchUsers}>
            <FaRedoAlt aria-hidden="true" />
            Refresh
          </button>
          <button className="btn usr-secondary-action" type="button" onClick={resetForm}>
            <FaTimes aria-hidden="true" />
            Reset
          </button>
          <button className="btn usr-primary-action" type="button" onClick={saveUser}>
            <FaPlus aria-hidden="true" />
            {saving ? "Saving..." : "Create User"}
          </button>
        </div>
      </section>

      <section className="usr-summary-grid">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <article className={`usr-summary-card ${card.tone}`} key={card.label}>
              <div className="usr-summary-icon">
                <Icon aria-hidden="true" />
              </div>
              <div className="usr-summary-copy">
                <span>{card.label}</span>
                <strong>{card.value}</strong>
                <em>{card.helper}</em>
              </div>
            </article>
          );
        })}
      </section>

      <section className="card-like usr-form-card">
        <div className="usr-card-head">
          <div>
            <span>Account Setup</span>
            <h3>Add a new user</h3>
          </div>
          <div className="usr-top-actions">
            <button type="button" className="btn btn-sm btn-light usr-reset-btn" onClick={resetForm}>
              Clear
            </button>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-lg-3 col-md-6">
            <label className="form-label">Name</label>
            <input
              className="form-control"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="col-lg-3 col-md-6">
            <label className="form-label">Phone</label>
            <input
              className="form-control"
              placeholder="Mobile number"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <div className="col-lg-3 col-md-6">
            <label className="form-label">Password</label>
            <input
              className="form-control"
              placeholder="Set password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </div>
          <div className="col-lg-2 col-md-6">
            <label className="form-label">Role</label>
            <select
              className="form-select"
              value={form.role}
              onChange={(e) => handleRoleChange(e.target.value)}
            >
              {roleOptions.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-lg-1 col-md-6 d-grid align-items-end">
            <button className="btn btn-success usr-save-btn" onClick={saveUser} disabled={saving}>
              <FaSave aria-hidden="true" />
              Save
            </button>
          </div>

          <div className="col-lg-4 col-md-6">
            <label className="form-label">Search</label>
            <div className="usr-search-shell">
              <FaSearch aria-hidden="true" />
              <input
                className="form-control"
                placeholder="Search name, phone or role"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <label className="form-label">Filter role</label>
            <select
              className="form-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All roles</option>
              {roleOptions.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="card-like usr-table-card">
        <div className="usr-card-head">
          <div>
            <span>User Registry</span>
            <h3>Accounts available for employee linking</h3>
          </div>
          <div className="usr-table-meta">
            <span>{filteredUsers.length} rows</span>
            <strong>{loading ? "Loading..." : `${currentPage}/${totalPages}`}</strong>
          </div>
        </div>

        <div className="table-responsive usr-table-wrap">
          <table className="table usr-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((u) => (
                <tr key={`${u.source_table || 'tenant_users'}-${u.id}`}>
                  <td data-label="Name">{u.name}</td>
                  <td data-label="Phone">{u.phone}</td>
                  <td data-label="Role">
                    <span className={`usr-role-pill ${getRoleTone(u.role)}`}>{u.role}</span>
                  </td>
                  <td data-label="Action" className="usr-row-actions">
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => requestDelete(u)}
                    >
                      <FaTrash aria-hidden="true" />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {!currentUsers.length && (
                <tr>
                  <td colSpan="4">
                    <div className="usr-empty-table">
                      <FaUsers aria-hidden="true" />
                      <strong>No users found</strong>
                      <span>Create a user first so the employee screen can link to it.</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredUsers.length > 0 && (
          <div className="usr-pagination-bar">
            <button
              type="button"
              className="usr-page-btn"
              disabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              Prev
            </button>

            <div className="usr-page-list">
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`usr-page-btn ${currentPage === page ? "active" : ""}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="usr-page-btn"
              disabled={currentPage === totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>

      <DeleteConfirm
        open={!!deleteTarget}
        name={deleteTarget?.name}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        saving={saving}
      />
    </div>
  );
}
